/**
 * Ant Design 主题配置
 * 定义亮色和暗色主题的 token
 */
import { theme } from 'antd';

const { defaultAlgorithm, darkAlgorithm } = theme;

// 共享的设计 token
const sharedToken = {
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 4,
  fontFamily: `-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif`,
  fontSize: 14,
  motion: true,
};

// 亮色主题
export const lightTheme = {
  algorithm: defaultAlgorithm,
  token: {
    ...sharedToken,
    colorPrimary: '#F5A523',
    colorSuccess: '#52c41a',
    colorWarning: '#faad14',
    colorError: '#ff4d4f',
    colorInfo: '#1677ff',
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f5f7fa',
    colorBgElevated: '#ffffff',
    colorBorder: '#e5e7eb',
    colorBorderSecondary: '#f0f0f0',
    colorText: 'rgba(0, 0, 0, 0.88)',
    colorTextSecondary: 'rgba(0, 0, 0, 0.65)',
    colorTextTertiary: 'rgba(0, 0, 0, 0.45)',
    colorTextQuaternary: 'rgba(0, 0, 0, 0.25)',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
    boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
  },
  components: {
    Layout: {
      headerBg: '#ffffff',
      siderBg: '#ffffff',
      bodyBg: '#f0f2f5',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(245, 166, 35, 0.08)',
      itemHoverBg: 'rgba(0, 0, 0, 0.04)',
      itemSelectedColor: '#F5A523',
      groupTitleColor: 'rgba(0, 0, 0, 0.45)',
    },
    Card: {
      colorBgContainer: '#ffffff',
      boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.03), 0 1px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px 0 rgba(0, 0, 0, 0.02)',
    },
    Table: {
      headerBg: '#fafafa',
      rowHoverBg: '#fafafa',
    },
    Button: {
      primaryShadow: '0 2px 0 rgba(245, 166, 35, 0.1)',
    },
  },
};

// 暗色主题
export const darkTheme = {
  algorithm: darkAlgorithm,
  token: {
    ...sharedToken,
    colorPrimary: '#E8A847',
    colorSuccess: '#49aa19',
    colorWarning: '#d89614',
    colorError: '#d32029',
    colorInfo: '#177ddc',
    colorBgContainer: '#141414',
    colorBgLayout: '#000000',
    colorBgElevated: '#1f1f1f',
    colorBorder: '#434343',
    colorBorderSecondary: '#303030',
    colorText: 'rgba(255, 255, 255, 0.88)',
    colorTextSecondary: 'rgba(255, 255, 255, 0.65)',
    colorTextTertiary: 'rgba(255, 255, 255, 0.45)',
    colorTextQuaternary: 'rgba(255, 255, 255, 0.25)',
    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.3), 0 1px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    boxShadowSecondary: '0 6px 16px 0 rgba(0, 0, 0, 0.4), 0 3px 6px -4px rgba(0, 0, 0, 0.3), 0 9px 28px 8px rgba(0, 0, 0, 0.2)',
  },
  components: {
    Layout: {
      headerBg: '#141414',
      siderBg: '#141414',
      bodyBg: '#000000',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: 'rgba(232, 168, 71, 0.15)',
      itemHoverBg: 'rgba(255, 255, 255, 0.08)',
      itemSelectedColor: '#E8A847',
      groupTitleColor: 'rgba(255, 255, 255, 0.45)',
      darkItemBg: 'transparent',
      darkItemSelectedBg: 'rgba(232, 168, 71, 0.15)',
      darkItemHoverBg: 'rgba(255, 255, 255, 0.08)',
    },
    Card: {
      colorBgContainer: '#141414',
      boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.3), 0 1px 6px -1px rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    },
    Table: {
      headerBg: '#1f1f1f',
      rowHoverBg: '#262626',
    },
    Button: {
      primaryShadow: '0 2px 0 rgba(232, 168, 71, 0.2)',
    },
    Input: {
      colorBgContainer: '#1f1f1f',
    },
    Select: {
      colorBgContainer: '#1f1f1f',
    },
    Modal: {
      contentBg: '#1f1f1f',
      headerBg: '#1f1f1f',
    },
    Drawer: {
      colorBgElevated: '#1f1f1f',
    },
    Dropdown: {
      colorBgElevated: '#1f1f1f',
    },
  },
};

// 获取主题配置
export const getThemeConfig = (isDark) => {
  return isDark ? darkTheme : lightTheme;
};

const themeConfig = { lightTheme, darkTheme, getThemeConfig };

export default themeConfig;
